import { fromJS } from 'immutable';

import {
  DELETE_TASK_SUCCESS,
  LOAD_DISMISS_USER_SUCCESS,
  LOAD_PROJECT_BY_ID_SUCCESS,
  LOAD_TASK_BY_ID_SUCCESS,
  LOAD_TASK_LABELS_SUCCESS,
  LOAD_TASK_STATUSES_SUCCESS,
  LOAD_TASKS_SUCCESS,
  LOAD_DRAWER_TASKS_SUCCESS,
  LOAD_SUBTASKS_LIST_SUCCESS,
  LOAD_TIMELOGS_SUCCESS,
  LOAD_TASK_TIMELOGS_SUCCESS,
  UPDATE_TASK_SUCCESS,
  UPDATE_TIME_SUCCESS,
  LOAD_TASK_LABEL_GROUPS_SUCCESS,
  LOAD_WORKFLOWS_SUCCESS,
  UPDATE_TASK_STATUS_SUCCESS,
  UPDATE_TASK_LABEL_SUCCESS,
  DELETE_TASK_STATUS_SUCCESS,
  DELETE_TASK_LABEL_SUCCESS,
  UPDATE_WORKFLOW_SUCCESS,
  UPDATE_TASK_LABEL_GROUP_SUCCESS,
  DELETE_WORKFLOW_SUCCESS,
  DELETE_TASK_LABEL_GROUP_SUCCESS,
  DELETE_TIMER_SUCCESS,
  STOP_TIMER_SUCCESS,
  ADD_PROJECT_FAVORITE_SUCCESS,
  DELETE_PROJECT_FAVORITE_SUCCESS,
  TASK_SEND_EMOJI_SUCCESS,
  LOAD_TASK_LIST_SUCCESS,
  DELETE_TIMER_GROUP_SUCCESS,
  DELETE_FILE_SUCCESS,
  //Checklist constants
  UPDATE_CHECKLIST_SUCCESS,
  DELETE_CHECKLIST_SUCCESS,
  UPDATE_CHECKLIST_ITEM_SUCCESS,
  DELETE_CHECKLIST_ITEM_SUCCESS,
  CHECK_CHECKLIST_ITEM_SUCCESS,
  TASK_VIEW_COMMENT_NOTIFICATIONS_SUCCESS,
  UPDATE_TIME_GROUP_SUCCESS,
  LOAD_TASKS_MIGRATE_SUCCESS,
  LOAD_TASKS_COMMENT_MIGRATE_SUCCESS,
  LOAD_BOARD_TASKS_SUCCESS,
  UPDATE_TASKS_SUCCESS,
  DRAG_CHECKLIST_ITEM_SUCCESS,
  DRAG_CHECKLIST_ITEM,
  // Task templates
  LOAD_TASK_TEMPLATES_SUCCESS,
  LOAD_TASK_TEMPLATE_SUCCESS,
  UPDATE_TASK_TEMPLATE_SUCCESS,
  DELETE_TASK_TEMPLATE_SUCCESS,
  // Task schedules
  UPDATE_TASK_SCHEDULE_SUCCESS,
  LOAD_SCHEDULE_TASKS_SUCCESS,
  LOAD_TASKS_TREE_SUCCESS,
  CLEAR_TASK_LABELS,
  CLEAR_TASK_STATUSES,
  APPEND_ACTIVITY_LOG,
  CHANGE_STATUS_POSITION_SUCCESS,
  LOAD_TASK_STATUSES_COUNTERS_SUCCESS,
  LOAD_TASK_SCHEDULE_SUCCESS,
} from 'constants/ActionTypes';
import { mergeEntity, normalizeEntities } from 'helpers/utilHelpers';
import { appendActivityLogHandler } from 'helpers/reducers/taskReducerHelpers';

const initialState = fromJS({
  tasks: {},
  taskStatuses: {},
  taskLabels: {},
  workflows: {},
  taskLabelGroups: {},
  timelogs: {},
  checklists: {},
  checklistItems: {},
  userTasksIds: [],
  taskTemplates: {},
  taskSchedules: {},
});

/**
 * Function for change array field: `labels` in task
 * @param task
 * @param statuses - array
 * @param labels - array
 * @param tasks - array
 * @return new task
 */
const prepareTask = (task, tasks = null) => {
  const excludingTask = ['taskStatus', 'responsible', 'creator', 'coPerformer', 'checklist', 'schedule', 'parent'];

  if (task.checklist) {
    task.checklistId = task.checklist.listId;
  }

  if (task.parent && tasks) {
    tasks.push(prepareTask(task.parent, tasks));
  }

  if (task.subTasks && tasks) {
    task.subTasks = task.subTasks.map((subTask) => {
      tasks && tasks.push(prepareTask({ ...subTask }, tasks));
      return subTask.taskId;
    });
  }

  if (task.schedule) {
    task.scheduleId = task.schedule.scheduleId;
  }

  if (task?.files?.length) {
    task.files = task.files.map((file) => file.fileId);
  }

  excludingTask.forEach((field) => task[field] && delete task[field]);

  return task;
};

/**
 * Function for change array field: `listItems` in checklist
 * @param checklist
 * @param items - array
 * @return new checklist
 */
const prepareChecklist = (checklist, items) => {
  if (checklist.listItems) {
    checklist.listItems = checklist.listItems.map((item) => {
      items && items.push(item);
      return item.itemId;
    });
  }

  return checklist;
};

const taskReducer = (state = initialState, action) => {
  let checklistItems = [];
  const tasks = [];
  switch (action.type) {
    case LOAD_TASK_LABELS_SUCCESS:
      return state.set('taskLabels', normalizeEntities(action.labels.items, 'labelId'));
    case CLEAR_TASK_LABELS: {
      return state.set('taskLabels', initialState.get('taskLabels'));
    }
    case LOAD_TASK_STATUSES_SUCCESS: {
      return state.set('taskStatuses', normalizeEntities(action.statuses.items, 'statusId', state.get('taskStatuses')));
    }

    case CLEAR_TASK_STATUSES: {
      return state.set('taskStatuses', initialState.get('taskStatuses'));
    }
    case LOAD_PROJECT_BY_ID_SUCCESS: {
      let newState = state;
      if (action.project.tasks) {
        const tasks = action.project.tasks.map((task) => prepareTask({ ...task }, []));
        newState = newState.set('tasks', normalizeEntities(tasks, 'taskId', newState.get('tasks')));
      }

      return newState;
    }

    case LOAD_TASKS_MIGRATE_SUCCESS:
    case LOAD_TASKS_COMMENT_MIGRATE_SUCCESS:
    case LOAD_TASK_LIST_SUCCESS:
    case LOAD_TASKS_TREE_SUCCESS:
    case LOAD_DRAWER_TASKS_SUCCESS:
    case LOAD_TASKS_SUCCESS: {
      action.tasks.items.forEach((task) => {
        tasks.push(prepareTask({ ...task }, tasks));
      });

      let taskIds = [];
      if (action.saveIds) {
        taskIds = action.tasks.items.map((task) => task.taskId);
      }
      return state
        .set('tasks', normalizeEntities(tasks, 'taskId', state.get('tasks')))
        .set('userTasksIds', fromJS(taskIds));
    }
    case LOAD_BOARD_TASKS_SUCCESS: {
      action.tasks.items.forEach((task) => {
        tasks.push(prepareTask({ ...task }, tasks));
      });
      return state.set('tasks', normalizeEntities(tasks, 'taskId', state.get('tasks')));
    }
    case LOAD_SUBTASKS_LIST_SUCCESS:
    case LOAD_TASK_BY_ID_SUCCESS: {
      let newState = state;
      if (action.task.checklist) {
        const checklist = prepareChecklist({ ...action.task.checklist }, checklistItems);

        newState = newState
          .setIn(['checklists', checklist.listId], fromJS(checklist))
          .set('checklistItems', normalizeEntities(checklistItems, 'itemId', newState.get('checklistItems')));
      }
      let task = prepareTask({ ...action.task }, tasks);
      return newState
        .set('tasks', normalizeEntities(tasks, 'taskId', newState.get('tasks')))
        .setIn(['tasks', task.taskId], mergeEntity(task, newState.getIn(['tasks', task.taskId])));
    }

    case LOAD_DISMISS_USER_SUCCESS: {
      if (action.user.tasks) {
        const tasks = action.user.tasks.map((task) => prepareTask({ ...task }));

        return state.set('tasks', normalizeEntities(tasks, 'taskId', state.get('tasks')));
      }

      return state;
    }
    case LOAD_TASK_TIMELOGS_SUCCESS:
    case LOAD_TIMELOGS_SUCCESS: {
      return state.set('timelogs', normalizeEntities(action.timelogs.items, 'timeId', state.get('timelogs')));
    }
    case UPDATE_TASK_SUCCESS: {
      let newState = state;
      //Update checklist
      if (action.task.checklist) {
        const checklist = prepareChecklist({ ...action.task.checklist }, checklistItems);

        newState = newState
          .setIn(['checklists', checklist.listId], fromJS(checklist))
          .set('checklistItems', normalizeEntities(checklistItems, 'itemId', newState.get('checklistItems')));
      }

      // Delete checklist
      if (action.task.checklist === null) {
        const checklist = state.get('checklists').find((list) => list.get('taskId') === action.task.taskId);

        if (checklist && checklist.get('listItems')) {
          newState = newState.deleteIn(['checklists', checklist.get('listId')]);
          checklist.get('listItems').forEach((id) => {
            newState = newState.deleteIn(['checklistItems', id]);
          });
        }
      }

      let task = prepareTask({ ...action.task }, tasks);
      newState = newState
        .set('tasks', normalizeEntities(tasks, 'taskId', newState.get('tasks')))
        .setIn(['tasks', task.taskId], mergeEntity(task, newState.getIn(['tasks', task.taskId])));

      return newState;
    }
    case UPDATE_TIME_SUCCESS: {
      let newState = state;
      let task = state.getIn(['tasks', action.values.taskId]);

      if (task) {
        const timelog = state.getIn(['timelogs', action.values.timeId]);
        const count = timelog ? action.values.count - timelog.get('count') : action.values.count;

        if (task.get('timeCount')) {
          task = task.set('timeCount', task.get('timeCount') + count);
        } else {
          task = task.set('timeCount', count);
        }
        newState = state.setIn(['tasks', task.get('taskId')], task);
      }

      return newState.setIn(['timelogs', action.values.timeId], fromJS(action.values));
    }
    case UPDATE_TIME_GROUP_SUCCESS: {
      let newState = state;

      newState = newState.setIn(['timelogs', action.values.time.timeId], fromJS(action.values.time));

      let task = newState.getIn(['tasks', action.values.time.taskId]);

      action.values.updatedIds.forEach((id) => {
        const timelog = state.getIn(['timelogs', parseInt(id)]);
        if (task.get('timeCount') && timelog) {
          task = task.set('timeCount', parseInt(task.get('timeCount')) - parseInt(timelog.get('count')));
        }
        newState = newState.deleteIn(['timelogs', parseInt(id)]);
      });

      if (task) {
        if (task.get('timeCount')) {
          task = task.set('timeCount', parseInt(task.get('timeCount')) + parseInt(action.values.time.count));
        }
        newState = newState.setIn(['tasks', task.get('taskId')], task);
      }

      return newState;
    }
    case STOP_TIMER_SUCCESS: {
      let task = state.getIn(['tasks', action.values.taskId]);

      if (task.get('timeCount')) {
        task = task.set('timeCount', task.get('timeCount') + action.values.count);
      } else {
        task = task.set('timeCount', action.values.count);
      }

      return state
        .setIn(['tasks', task.get('taskId')], task)
        .setIn(['timelogs', action.values.timeId], fromJS(action.values));
    }
    case DELETE_TIMER_SUCCESS: {
      const timelog = state.getIn(['timelogs', action.timeId]);

      if (timelog) {
        let task = state.getIn(['tasks', timelog.get('taskId')]);

        if (task.get('timeCount')) {
          task = task.set('timeCount', task.get('timeCount') - timelog.get('count'));
        }

        return state.setIn(['tasks', task.get('taskId')], task).deleteIn(['timelogs', action.timeId]);
      }

      return state;
    }
    case DELETE_TIMER_GROUP_SUCCESS: {
      let newState = state;
      if (action.timers?.length) {
        action.timers.forEach((timer) => {
          const timelog = state.getIn(['timelogs', timer.timeId]);

          if (!timelog) {
            return state;
          }

          newState = newState.deleteIn(['timelogs', timelog.get('timeId')]);

          let task = state.getIn(['tasks', timelog.get('taskId')]);

          if (task) {
            if (task.get('timeCount')) {
              task = task.set('timeCount', task.get('timeCount') - timelog.get('count'));
            }

            newState = newState.setIn(['tasks', task.get('taskId')], task);
          }
        });
      }
      return newState;
    }
    case DELETE_TASK_SUCCESS: {
      const checklistId = state.getIn(['tasks', action.taskId, 'checklistId']);
      let newState = state;

      if (checklistId) {
        const checklist = newState.getIn(['checklists', checklistId]);

        if (checklist && checklist.get('listItems')) {
          newState = newState.deleteIn(['checklists', checklistId]);
          checklist.get('listItems').forEach((id) => {
            newState = newState.deleteIn(['checklistItems', id]);
          });
        }
      }

      return newState.deleteIn(['tasks', action.taskId]);
    }
    case LOAD_TASK_LABEL_GROUPS_SUCCESS: {
      return state.set('taskLabelGroups', normalizeEntities(action.values.items, 'labelGroupId'));
    }
    case LOAD_WORKFLOWS_SUCCESS: {
      return state.set('workflows', normalizeEntities(action.values.items, 'workflowId'));
    }
    case UPDATE_TASK_STATUS_SUCCESS: {
      return state.setIn(['taskStatuses', action.values.statusId], fromJS(action.values));
    }

    case LOAD_TASK_STATUSES_COUNTERS_SUCCESS: {
      let newState = state;

      action.values.items.forEach(({ statusId, taskCount }) => {
        newState = newState.setIn(['taskStatuses', statusId, 'taskCount'], taskCount);
      });

      return newState;
    }

    case CHANGE_STATUS_POSITION_SUCCESS: {
      let newState = state;

      Object.keys(action.payload).forEach((workflowId) => {
        action.payload[workflowId].forEach(({ statusId, position }) => {
          const statusPath = ['taskStatuses', statusId];
          newState = newState
            .setIn([...statusPath, 'position'], position)
            .setIn([...statusPath, 'workflowId'], parseInt(workflowId));
        });
      });

      return newState;
    }
    case UPDATE_TASK_LABEL_SUCCESS: {
      if (state.getIn(['taskLabels', action.values.labelId])) {
        return state.setIn(['taskLabels', action.values.labelId], fromJS(action.values));
      }

      const groupLabels = state.getIn(['taskLabelGroups', action.values.labelGroupId, 'taskLabels']);
      return state
        .setIn(['taskLabels', action.values.labelId], fromJS(action.values))
        .setIn(
          ['taskLabelGroups', action.values.labelGroupId, 'taskLabels'],
          groupLabels ? groupLabels.push(action.values.labelId) : fromJS([action.values.labelId]),
        );
    }
    case UPDATE_WORKFLOW_SUCCESS: {
      return state.setIn(
        ['workflows', action.values.workflowId],
        mergeEntity(action.values, state.getIn(['workflows', action.values.workflowId])),
      );
    }
    case UPDATE_TASK_LABEL_GROUP_SUCCESS: {
      return state.setIn(
        ['taskLabelGroups', action.values.labelGroupId],
        mergeEntity(action.values, state.getIn(['taskLabelGroups', action.values.labelGroupId])),
      );
    }
    case DELETE_TASK_STATUS_SUCCESS: {
      const updateTasks = state.get('tasks').map((task) => {
        const statusId = task.get('statusId');
        if (statusId && statusId === action.statusId) {
          return task.set('statusId', null);
        }

        return task;
      });

      return state.set('tasks', updateTasks).deleteIn(['taskStatuses', action.statusId]);
    }
    case DELETE_TASK_LABEL_SUCCESS: {
      const label = state.getIn(['taskLabels', action.labelId]);
      let groupLabels = state.getIn(['taskLabelGroups', label.get('labelGroupId'), 'taskLabels']);

      const batchUpdateTasks = state.get('tasks').map((task) => {
        if (task.get('labels') && task.get('labels').find((id) => id === action.labelId)) {
          const taskLabels = task.get('labels').filter((id) => id !== action.labelId);
          return task.set('labels', taskLabels);
        }

        return task;
      });

      return state
        .set('tasks', batchUpdateTasks)
        .deleteIn(['taskLabels', action.labelId])
        .setIn(
          ['taskLabelGroups', label.get('labelGroupId'), 'taskLabels'],
          groupLabels ? groupLabels.filter((id) => id !== action.labelId) : fromJS([]),
        );
    }
    case DELETE_WORKFLOW_SUCCESS: {
      const statusIds = state
        .get('taskStatuses')
        .filter((status) => status.get('workflowId') === action.workflowId)
        .map((status) => status.get('statusId'));
      const updatedTasks = state.get('tasks').map((task) => {
        const statusId = task.get('statusId');
        if (statusId && statusIds.includes(statusId)) {
          return task.set('statusId', null);
        }

        return task;
      });

      return state.set('tasks', updatedTasks).deleteIn(['workflows', action.workflowId]);
    }
    case DELETE_TASK_LABEL_GROUP_SUCCESS: {
      const groupLabels = state.getIn(['taskLabelGroups', action.labelGroupId, 'taskLabels']);
      let newState = state;

      if (groupLabels) {
        groupLabels.forEach((labelId) => {
          newState = newState.deleteIn(['taskLabels', labelId]);
        });
      }

      const batchUpdateTasks = state.get('tasks').map((task) => {
        if (task.get('labels') && groupLabels && task.get('labels').find((id) => groupLabels.includes(id))) {
          const taskLabels = task.get('labels').filter((id) => !groupLabels.includes(id));
          return task.set('labels', taskLabels);
        }

        return task;
      });

      return newState.set('tasks', batchUpdateTasks).deleteIn(['taskLabelGroups', action.labelGroupId]);
    }
    case ADD_PROJECT_FAVORITE_SUCCESS:
      if (action.favoriteMap.entity === 'task') {
        return state.setIn(['tasks', action.favoriteMap.entityId, 'favorite'], true);
      }
      return state;
    case DELETE_PROJECT_FAVORITE_SUCCESS:
      if (action.values.entity === 'task') {
        return state.setIn(['tasks', action.values.entityId, 'favorite'], false);
      }
      return state;
    case TASK_SEND_EMOJI_SUCCESS:
      return state.setIn(['tasks', action.values.taskId, 'reactions'], fromJS(action.values.reactions));
    case UPDATE_CHECKLIST_SUCCESS: {
      const checklist = prepareChecklist({ ...action.values });
      const task = state.getIn(['tasks', checklist.taskId]);

      if (task) {
        return state
          .setIn(['tasks', checklist.taskId, 'checklistId'], checklist.listId)
          .setIn(['checklists', checklist.listId], fromJS(checklist));
      }

      return state;
    }
    case DELETE_CHECKLIST_SUCCESS: {
      const checklist = state.getIn(['checklists', action.values]);
      let newState = state;

      checklist.get('listItems').forEach((id) => {
        newState = newState.deleteIn(['checklistItems', id]);
      });

      newState = newState.setIn(['tasks', action.taskId, 'checklistId'], null);

      return newState.deleteIn(['checklists', action.values]);
    }
    case CHECK_CHECKLIST_ITEM_SUCCESS:
    case UPDATE_CHECKLIST_ITEM_SUCCESS: {
      let listItems = state.getIn(['checklists', action.values.listId, 'listItems']);

      if (!listItems.includes(action.values.itemId)) {
        listItems = listItems.push(action.values.itemId);
      }

      return state
        .setIn(['checklistItems', action.values.itemId], fromJS(action.values))
        .setIn(['checklists', action.values.listId, 'listItems'], listItems);
    }
    case DELETE_CHECKLIST_ITEM_SUCCESS: {
      const listId = state.getIn(['checklistItems', action.values, 'listId']);
      const listItems = state.getIn(['checklists', listId, 'listItems']);

      return state
        .setIn(
          ['checklists', listId, 'listItems'],
          listItems.filter((id) => id !== action.values),
        )
        .deleteIn(['checklistItems', action.values]);
    }
    case TASK_VIEW_COMMENT_NOTIFICATIONS_SUCCESS:
      return state
        .setIn(['tasks', action.taskId, 'hasNewComments'], false)
        .setIn(['tasks', action.taskId, 'hasNewFeeds'], false);
    case UPDATE_TASKS_SUCCESS: {
      if (!action.isDelete) {
        return state.set('tasks', normalizeEntities(action.tasks.items, 'taskId', state.get('tasks')));
      }

      let newState = state;
      action.tasks.items.forEach((task) => {
        newState = newState.deleteIn(['tasks', task.taskId]);
      });
      return newState;
    }
    case DELETE_FILE_SUCCESS:
      if (action.entity && action.entity === 'task' && action.entityId) {
        const files = state.getIn(['tasks', action.entityId, 'files']);
        if (files) {
          return state.setIn(
            ['tasks', action.entityId, 'files'],
            files.filter((fileId) => fileId !== action.fileId),
          );
        }
      }
      return state;
    case DRAG_CHECKLIST_ITEM_SUCCESS:
      return state.set('checklistItems', normalizeEntities(action.values, 'itemId', state.get('checklistItems')));
    case DRAG_CHECKLIST_ITEM: {
      return state.set(
        'checklistItems',
        normalizeEntities(action.reducerValues.items, 'itemId', state.get('checklistItems')),
      );
    }

    // Task template cases
    // -------------------
    case LOAD_TASK_TEMPLATES_SUCCESS: {
      let newState = state;
      action.values.items.forEach((template) => {
        const statePath = ['taskTemplates', template.templateId];
        if (!newState.getIn(statePath)) {
          newState = newState.setIn(statePath, fromJS(template));
        }
      });

      return newState;
    }
    case LOAD_TASK_TEMPLATE_SUCCESS: {
      let newState = state;
      if (!state.getIn(['taskTemplates', action.value.templateId])) {
        newState = newState.setIn(['taskTemplates', action.value.templateId], fromJS(action.value));
      }
      return newState;
    }
    case UPDATE_TASK_TEMPLATE_SUCCESS: {
      return state.setIn(['taskTemplates', action.value.templateId], fromJS(action.value));
    }
    case DELETE_TASK_TEMPLATE_SUCCESS: {
      return state.deleteIn(['taskTemplates', action.entityId]);
    }
    case LOAD_TASK_SCHEDULE_SUCCESS: {
      return state.set(
        'taskSchedules',
        normalizeEntities(action.schedules.items, 'scheduleId', state.get('taskSchedules')),
      );
    }
    case UPDATE_TASK_SCHEDULE_SUCCESS: {
      return state.setIn(['tasks', action.taskId, 'scheduleId'], action.schedule.scheduleId);
    }
    case LOAD_SCHEDULE_TASKS_SUCCESS: {
      action.tasks.items.forEach((task) => {
        tasks.push(prepareTask({ ...task }, tasks));
      });

      return state.set('tasks', normalizeEntities(tasks, 'taskId', state.get('tasks')));
    }

    case APPEND_ACTIVITY_LOG: {
      return appendActivityLogHandler(state, action, prepareTask);
    }

    default:
      return state;
  }
};

export default taskReducer;
